import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevices } from '../store/devices';
import { useAuth } from '../store/auth';
import toast from 'react-hot-toast';

const card = {
  background:'var(--surface)', border:'1px solid var(--border)',
  borderRadius:'var(--radius)', padding:16, marginBottom:12,
  cursor:'pointer', transition:'border-color .15s',
};

export default function Dashboard() {
  const { devices, fetch, select, softDelete, hardDelete, restoreDevice, transferDevice } = useDevices();
  const { claims } = useAuth();
  const isSuper = !!claims?.super;
  const navigate = useNavigate();

  useEffect(() => { fetch(); }, []);

  // Modal state for transfer and confirmations
  const [modal, setModal] = useState(null); // { type: 'transfer'|'confirm', deviceId, title, onConfirm }
  const [transferToken, setTransferToken] = useState('');

  const online = devices.filter(d => d.is_online);
  const offline = devices.filter(d => !d.is_online);

  const open = (d) => { select(d); navigate(`/device/${d.device_id}`); };

  const bulkShutdown = () => {
    setModal({
      type: 'confirm', title: `Shutdown all ${online.length} online devices?`,
      onConfirm: async () => {
        const ids = online.map(d => d.device_id);
        const { bulkCommand } = useDevices.getState();
        await bulkCommand('power', { device_ids: ids, action: 'shutdown' });
        toast.success('Shutdown command sent to all online devices');
        setModal(null);
      },
    });
  };

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <h2 style={{ fontSize:20, fontWeight:700 }}>All Devices</h2>
        <div style={{ display:'flex', gap:8 }}>
          <Btn onClick={fetch} muted>Refresh</Btn>
          <Btn onClick={bulkShutdown} danger>Bulk Shutdown</Btn>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
        <Stat label="Total" value={devices.length} />
        <Stat label="Online" value={online.length} color="var(--green)" />
        <Stat label="Offline" value={offline.length} color="var(--muted)" />
        <Stat label="Groups" value={[...new Set(devices.map(d=>d.group_name).filter(Boolean))].length} />
      </div>

      {/* Status legend */}
      <div style={{ display:'flex', gap:20, marginBottom:16, flexWrap:'wrap',
                    padding:'10px 14px', background:'var(--surface)',
                    border:'1px solid var(--border)', borderRadius:8, fontSize:12 }}>
        <span style={{ color:'var(--muted)', fontWeight:600, marginRight:4 }}>Status:</span>
        {[
          { dots: ['var(--green)', 'var(--green)'], label: 'Fully online' },
          { dots: ['var(--green)', '#f59e0b'],      label: 'System only (user logged out)' },
          { dots: ['var(--muted)', 'var(--muted)'], label: 'Offline' },
        ].map(({ dots, label }) => (
          <span key={label} style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ display:'flex', gap:3 }}>
              {dots.map((c, i) => (
                <span key={i} style={{ width:9, height:9, borderRadius:'50%', background:c, display:'inline-block' }} />
              ))}
            </span>
            <span style={{ color:'var(--muted)' }}>{label}</span>
          </span>
        ))}
        <span style={{ color:'var(--muted)', marginLeft:'auto', fontSize:11 }}>
          Left dot = SYSTEM service &nbsp;·&nbsp; Right dot = User session
        </span>
      </div>

      {/* Device grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:12 }}>
        {devices.map(d => (
          <DeviceCard
            key={d.device_id}
            d={d}
            isSuper={isSuper}
            onOpen={() => !d.deleted_by_admin && open(d)}
            onSoftDelete={() => {
              setModal({
                type: 'confirm', title: `Remove "${d.label || d.hostname}" from your dashboard?`,
                subtitle: 'You might be able to restore it if you contact Super Admin.',
                onConfirm: () => { softDelete(d.device_id); toast.success('Device removed'); setModal(null); },
              });
            }}
            onRestore={() => {
              restoreDevice(d.device_id);
              toast.success('Device restored to admin dashboard');
            }}
            onHardDelete={() => {
              setModal({
                type: 'confirm', title: `Permanently delete "${d.label || d.hostname}" everywhere?`,
                subtitle: 'This cannot be undone.',
                onConfirm: () => { hardDelete(d.device_id); toast.success('Device permanently deleted'); setModal(null); },
              });
            }}
            onTransfer={() => {
              setTransferToken('');
              setModal({
                type: 'transfer', deviceId: d.device_id,
                title: `Transfer "${d.label || d.hostname}" to another admin`,
              });
            }}
          />
        ))}
      </div>

      {/* ── Modal overlay ── */}
      {modal && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000,
          display:'flex', alignItems:'center', justifyContent:'center',
        }} onClick={() => setModal(null)}>
          <div style={{
            background:'var(--surface)', border:'1px solid var(--border)',
            borderRadius:12, padding:24, minWidth:360, maxWidth:440,
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom:8, fontSize:16 }}>{modal.title}</h3>
            {modal.subtitle && <p style={{ color:'var(--muted)', fontSize:13, marginBottom:16 }}>{modal.subtitle}</p>}

            {modal.type === 'transfer' && (
              <div style={{ marginBottom:16 }}>
                <label style={{ display:'block', fontSize:12, color:'var(--muted)', marginBottom:6 }}>Target Admin Agent Token</label>
                <input
                  value={transferToken}
                  onChange={e => setTransferToken(e.target.value)}
                  placeholder="Paste agent_token here..."
                  autoFocus
                  style={{
                    width:'100%', padding:'10px 12px', background:'var(--bg)',
                    border:'1px solid var(--border)', borderRadius:6,
                    color:'var(--text)', fontSize:13, fontFamily:'monospace',
                    boxSizing:'border-box',
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && transferToken.trim()) {
                      transferDevice(modal.deviceId, transferToken.trim())
                        .then(r => { toast.success('Transferred to ' + r.transferred_to); setModal(null); })
                        .catch(err => toast.error(err.response?.data?.detail || 'Transfer failed'));
                    }
                  }}
                />
              </div>
            )}

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setModal(null)} style={{
                padding:'8px 16px', borderRadius:6, border:'1px solid var(--border)',
                background:'transparent', color:'var(--text)', cursor:'pointer', fontSize:13,
              }}>Cancel</button>
              {modal.type === 'confirm' && (
                <button onClick={modal.onConfirm} style={{
                  padding:'8px 16px', borderRadius:6, border:'none',
                  background:'#ef4444', color:'#fff', cursor:'pointer', fontWeight:600, fontSize:13,
                }}>Confirm</button>
              )}
              {modal.type === 'transfer' && (
                <button onClick={() => {
                  if (!transferToken.trim()) { toast.error('Enter an agent token'); return; }
                  transferDevice(modal.deviceId, transferToken.trim())
                    .then(r => { toast.success('Transferred to ' + r.transferred_to); setModal(null); })
                    .catch(err => toast.error(err.response?.data?.detail || 'Transfer failed'));
                }} style={{
                  padding:'8px 16px', borderRadius:6, border:'none',
                  background:'#6366f1', color:'#fff', cursor:'pointer', fontWeight:600, fontSize:13,
                }}>Transfer</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DeviceCard({ d, isSuper, onOpen, onSoftDelete, onRestore, onHardDelete, onTransfer }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const isDeleted = d.deleted_by_admin;

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handle = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [menuOpen]);

  return (
    <div style={{
      ...card,
      position: 'relative',
      opacity: isDeleted ? 0.55 : 1,
      borderColor: isDeleted
        ? '#ef444466'
        : d.system_agent_online && d.user_agent_online ? 'var(--accent)'
        : d.system_agent_online ? '#f59e0b44'
        : 'var(--border)',
    }} onClick={onOpen}>

      {/* Deleted banner */}
      {isDeleted && (
        <div style={{
          position:'absolute', top:0, left:0, right:0,
          background:'#ef44441a', borderBottom:'1px solid #ef444433',
          padding:'4px 10px', borderRadius:'var(--radius) var(--radius) 0 0',
          display:'flex', alignItems:'center', gap:6, fontSize:11,
        }}>
          <span style={{ color:'#f87171', fontSize:13 }}>⊘</span>
          <span style={{ color:'#f87171', fontWeight:600 }}>Removed from admin dashboard</span>
        </div>
      )}

      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, marginTop: isDeleted ? 22 : 0 }}>
        {/* Two dots: left=SYSTEM, right=user agent */}
        <span style={{ display:'flex', gap:3, flexShrink:0 }}>
          <span style={{ width:10, height:10, borderRadius:'50%',
            background: d.system_agent_online ? 'var(--green)' : 'var(--muted)' }} />
          <span style={{ width:10, height:10, borderRadius:'50%',
            background: d.user_agent_online ? 'var(--green)'
                      : d.system_agent_online ? '#f59e0b' : 'var(--muted)' }} />
        </span>
        <span style={{ fontWeight:600 }}>{d.label || d.hostname} <span style={{ fontWeight:400, color:'var(--muted)', fontSize:11 }}>(ID:{d.device_id.slice(0,8)})</span></span>
        {d.platform === 'android' && (
          <span style={{ fontSize:10, padding:'1px 6px', borderRadius:3,
                         background:'#3ddc8422', border:'1px solid #3ddc8466', color:'#3ddc84',
                         fontWeight:700, flexShrink:0 }}>ANDROID</span>
        )}
        {d.group_name && <Tag>{d.group_name}</Tag>}
        {isSuper && d.admin_username && (
          <span style={{ marginLeft:'auto', fontSize:10, padding:'1px 6px', borderRadius:3,
                         background:'#7c3aed22', border:'1px solid #7c3aed55', color:'#a78bfa',
                         whiteSpace:'nowrap', flexShrink:0 }}>{d.admin_username}</span>
        )}
      </div>

      <Row k="User" v={d.username} />
      <Row k="OS" v={d.os_version?.split(' ').slice(0,3).join(' ')} />
      <Row k="IP" v={d.ip_address} />
      <Row k="Location" v={[d.city, d.state, d.country].filter(Boolean).join(', ') || '—'} />
      <Row k="Device Time" v={d.device_time ? `${d.device_time} (${d.timezone || ''})` : (d.timezone || '—')} />
      <Row k="RAM" v={d.ram_gb && `${d.ram_gb} GB`} />
      <Row k="Last seen" v={d.last_seen ? new Date(d.last_seen+'Z').toLocaleString() : '—'} />

      {/* ── Three-dots menu ── */}
      <div ref={menuRef} style={{ position:'absolute', bottom:10, right:10 }}
           onClick={e => e.stopPropagation()}>
        <button
          onClick={() => setMenuOpen(o => !o)}
          style={{ background:'none', border:'1px solid var(--border)', borderRadius:5,
                   color:'var(--muted)', cursor:'pointer', padding:'2px 8px',
                   fontSize:16, lineHeight:1 }}>
          ···
        </button>
        {menuOpen && (
          <div style={{
            position:'absolute', bottom:28, right:0, minWidth:210,
            background:'var(--surface)', border:'1px solid var(--border)',
            borderRadius:8, boxShadow:'0 4px 16px #0006', zIndex:99,
            overflow:'hidden',
          }}>
            {/* Remove from admin dashboard (both admin and super admin) */}
            {!isDeleted && (
              <MenuItem color="#f87171" onClick={() => { setMenuOpen(false); onSoftDelete(); }}>
                ✕ Remove from dashboard
              </MenuItem>
            )}
            {/* Super admin only */}
            {isSuper && isDeleted && (
              <MenuItem color="#4ade80" onClick={() => { setMenuOpen(false); onRestore(); }}>
                ↩ Restore to admin dashboard
              </MenuItem>
            )}
            {isSuper && (
              <MenuItem color="#a78bfa" onClick={() => { setMenuOpen(false); onTransfer(); }}>
                ↗ Transfer to another admin
              </MenuItem>
            )}
            {isSuper && (
              <MenuItem color="#ef4444" bold onClick={() => { setMenuOpen(false); onHardDelete(); }}>
                🗑 Delete everywhere
              </MenuItem>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MenuItem({ children, onClick, color, bold }) {
  return (
    <button onClick={onClick} style={{
      display:'block', width:'100%', textAlign:'left',
      padding:'9px 14px', background:'none', border:'none',
      color: color || 'var(--text)', cursor:'pointer', fontSize:13,
      fontWeight: bold ? 700 : 400,
      borderBottom:'1px solid var(--border)',
    }}
    onMouseEnter={e => e.currentTarget.style.background='var(--border)'}
    onMouseLeave={e => e.currentTarget.style.background='none'}>
      {children}
    </button>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)',
                  borderRadius:'var(--radius)', padding:16, textAlign:'center' }}>
      <div style={{ fontSize:28, fontWeight:700, color: color || 'var(--text)' }}>{value}</div>
      <div style={{ fontSize:12, color:'var(--muted)' }}>{label}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:2 }}>
      <span style={{ color:'var(--muted)' }}>{k}</span>
      <span>{v || '—'}</span>
    </div>
  );
}

function Tag({ children }) {
  return (
    <span style={{ fontSize:10, padding:'2px 6px', background:'var(--accent)20',
      color:'var(--accent)', borderRadius:4 }}>{children}</span>
  );
}

function Btn({ children, onClick, danger, muted }) {
  return (
    <button onClick={onClick} style={{
      padding:'6px 14px', border:'1px solid var(--border)', borderRadius:6,
      background: danger ? 'var(--red)20' : 'transparent',
      color: danger ? 'var(--red)' : muted ? 'var(--muted)' : 'var(--text)',
      cursor:'pointer', fontSize:13,
    }}>{children}</button>
  );
}
